/**
 * Owner insights (owner research P0-1 and P0-2): the Owner Weekly Brief and
 * the KPI anomaly alerts. Imported as `@velora/contracts/owner-insights`.
 *
 * Every number here comes from the Metric Dictionary (`@velora/domain/metrics`)
 * computed by the API. The local language model only words the brief; its
 * text is checked against these numbers and replaced by the plain template
 * when it does not match (see `wording` and `wordingNote`).
 *
 * Routes under `/v1/analytics`:
 * - `GET  /brief`                    this week's brief for the viewer's offices (built on first view)
 * - `POST /brief/regenerate`         build it again (numbers and wording)
 * - `GET  /alerts`                   KPI alerts for the viewer's offices (scans when stale)
 * - `POST /alerts/scan`              scan now
 * - `POST /alerts/:id/ack`           acknowledge an alert (audited)
 * - `GET|PUT /insights/subscriptions` the viewer's opt-in to the brief and alert emails
 *
 * Email and ntfy delivery go through the Automations & notifications gate
 * and are off until an administrator turns them on. No patient data is in
 * any brief, alert, email or ntfy message: office names, metric names,
 * counts, rates and money only.
 */
import { z } from 'zod';
import { MetricUnitSchema, ScopeInfoSchema, SnapshotInfoSchema } from './analytics.js';

/* ------------------------------------------------------------------------ */
/* KPI anomaly alerts                                                        */
/* ------------------------------------------------------------------------ */

export const KPI_ALERT_KINDS = [
  'collections_lag',
  'cancellation_spike',
  'ar_over_90',
  'production_per_hour_drop',
  'adjustment_spike',
  'writeoff_spike',
  'new_patient_drop',
  /** Day-end deleted-transactions watch (Hayden R11, roadmap Tier 1 #7): raised by the nightly pack. */
  'deleted_transactions',
  /** Nightly A/R check: Velora's Total A/R drifted from the PMS's own aging (roadmap Tier 1 #8). */
  'pms_drift',
  /** Error and embezzlement watch, high-severity alerts (roadmap Tier 1 #7). */
  'money_watch',
] as const;
export type KpiAlertKind = (typeof KPI_ALERT_KINDS)[number];
export const KpiAlertKindSchema = z.enum(KPI_ALERT_KINDS);

export const KpiAlertSeveritySchema = z.enum(['info', 'warning', 'critical']);
export type KpiAlertSeverity = z.infer<typeof KpiAlertSeveritySchema>;

export const KpiAlertStatusSchema = z.enum(['open', 'acknowledged', 'resolved']);
export type KpiAlertStatus = z.infer<typeof KpiAlertStatusSchema>;

export const EvidenceLinkSchema = z.object({
  label: z.string(),
  /** App path (never an external URL). */
  href: z.string().startsWith('/'),
});
export type EvidenceLink = z.infer<typeof EvidenceLinkSchema>;

export const KpiBaselineSchema = z.object({
  /** Median of the prior weeks, in the metric's unit. */
  median: z.number(),
  /** Robust spread (1.4826 x MAD, floored), in the metric's unit. */
  scale: z.number(),
  /** Weeks the baseline was built from. */
  points: z.number().int().nonnegative(),
});
export type KpiBaseline = z.infer<typeof KpiBaselineSchema>;

export const DeliveryStateSchema = z.enum(['sent', 'held', 'off']);
export type DeliveryState = z.infer<typeof DeliveryStateSchema>;

export const KpiAlertSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  kind: KpiAlertKindSchema,
  metricId: z.string(),
  severity: KpiAlertSeveritySchema,
  status: KpiAlertStatusSchema,
  weekStart: z.string(),
  weekEnd: z.string(),
  unit: MetricUnitSchema,
  value: z.number().nullable(),
  baseline: KpiBaselineSchema.nullable(),
  /** Robust z-score of the value against the baseline (null without a baseline). */
  z: z.number().nullable(),
  /** The fixed rule threshold, in the metric's unit, when the kind has one. */
  threshold: z.number().nullable(),
  ruleHit: z.boolean(),
  baselineHit: z.boolean(),
  title: z.string(),
  summary: z.string(),
  recommendedAction: z.string(),
  evidence: z.array(EvidenceLinkSchema),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  acknowledgedAt: z.string().nullable(),
  acknowledgedByName: z.string().nullable(),
  email: DeliveryStateSchema,
  ntfy: DeliveryStateSchema,
  deliveryNote: z.string().nullable(),
});
export type KpiAlert = z.infer<typeof KpiAlertSchema>;

export const KpiAlertRuleInfoSchema = z.object({
  kind: KpiAlertKindSchema,
  label: z.string(),
  metricId: z.string(),
  rule: z.string(),
  baseline: z.string(),
  money: z.boolean(),
  /**
   * The threshold the fixed rule judges against, when it has one the owner
   * can set (owner role test: alerts judged against a 90% / 98% rule nobody
   * had seen). Null for rules relative to the office's own history.
   */
  threshold: z.number().nullable().default(null),
  /** owner = set on the Goals page; goal = the collection rate goal; default = Velora's. */
  thresholdSource: z.enum(['owner', 'goal', 'default']).nullable().default(null),
  thresholdUnit: z.enum(['ratio', 'cents']).nullable().default(null),
  defaultThreshold: z.number().nullable().default(null),
});
export type KpiAlertRuleInfo = z.infer<typeof KpiAlertRuleInfoSchema>;

/** The owner's alert thresholds (practice default or one office); null removes one. */
export const AlertThresholdsPutSchema = z.object({
  locationId: z.uuid().nullable(),
  thresholds: z.partialRecord(KpiAlertKindSchema, z.number().min(0).max(100_000_000).nullable()),
});
export type AlertThresholdsPut = z.infer<typeof AlertThresholdsPutSchema>;

export const KpiAlertListQuerySchema = z.object({
  status: z.enum(['open', 'all']).optional(),
});
export type KpiAlertListQuery = z.infer<typeof KpiAlertListQuerySchema>;

export const KpiAlertListSchema = z.object({
  alerts: z.array(KpiAlertSchema),
  scannedAt: z.string().nullable(),
  /** The week the last scan judged (Monday to Sunday). */
  weekStart: z.string().nullable(),
  weekEnd: z.string().nullable(),
  scope: ScopeInfoSchema,
  rules: z.array(KpiAlertRuleInfoSchema),
  /** Offices whose money alerts are hidden (no money.view there). */
  moneyHiddenOffices: z.array(z.uuid()),
});
export type KpiAlertList = z.infer<typeof KpiAlertListSchema>;

export const KpiAlertAckResultSchema = z.object({
  id: z.uuid(),
  status: KpiAlertStatusSchema,
  acknowledgedAt: z.string().nullable(),
});
export type KpiAlertAckResult = z.infer<typeof KpiAlertAckResultSchema>;

export const KpiAlertScanResultSchema = z.object({
  scanned: z.boolean(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  resolved: z.number().int().nonnegative(),
});
export type KpiAlertScanResult = z.infer<typeof KpiAlertScanResultSchema>;

/* ------------------------------------------------------------------------ */
/* Owner Weekly Brief                                                        */
/* ------------------------------------------------------------------------ */

export const BriefFactUnitSchema = z.enum([
  'cents',
  'count',
  'ratio',
  'cents_per_hour',
  'cents_per_visit',
  'cents_per_patient',
  'cents_per_day',
  'points',
  'text',
]);
export type BriefFactUnit = z.infer<typeof BriefFactUnitSchema>;

export const BriefFactSchema = z.object({
  /** Placeholder key the wording uses, e.g. `n1` for `{n1}`; `office` for the office name. */
  key: z.string().regex(/^[a-z][a-z0-9]{0,15}$/),
  label: z.string(),
  /** Raw value in its unit (ratio 0.81, cents 1234500); null for text facts. */
  value: z.number().nullable(),
  unit: BriefFactUnitSchema,
  /** How the brief prints it ("81%", "$12,345", "Saginaw"). */
  formatted: z.string(),
  metricId: z.string().nullable(),
});
export type BriefFact = z.infer<typeof BriefFactSchema>;

export const BriefWordingSchema = z.enum(['local_llm', 'template']);
export type BriefWording = z.infer<typeof BriefWordingSchema>;

export const BriefDecisionSchema = z.object({
  id: z.string(),
  rank: z.number().int().positive(),
  topic: z.string(),
  locationId: z.uuid().nullable(),
  metricId: z.string(),
  direction: z.enum(['down', 'up', 'flat']),
  /** True when the change is good news (a decision to keep doing something). */
  positive: z.boolean(),
  headline: z.string(),
  question: z.string(),
  why: z.string(),
  /** The recommended action behind the question (fixed by the playbook, never by the model). */
  action: z.string(),
  facts: z.array(BriefFactSchema),
  evidence: z.array(EvidenceLinkSchema),
  wording: BriefWordingSchema,
  /** Why the model's wording was not used, when it was not. */
  wordingNote: z.string().nullable(),
  /** An open KPI alert behind this decision. */
  alertId: z.uuid().nullable(),
});
export type BriefDecision = z.infer<typeof BriefDecisionSchema>;

export const BriefEngineSchema = z.object({
  kind: BriefWordingSchema,
  /** Endpoint and model when the local model wrote any wording. */
  endpointId: z.string().nullable(),
  model: z.string().nullable(),
  note: z.string().nullable(),
});
export type BriefEngine = z.infer<typeof BriefEngineSchema>;

export const BriefEmailSchema = z.object({
  subscribed: z.boolean(),
  state: z.enum(['sent', 'held', 'off', 'not_yet']),
  note: z.string().nullable(),
});
export type BriefEmail = z.infer<typeof BriefEmailSchema>;

export const OwnerBriefQuerySchema = z.object({
  /** Any date in the week to show (Monday to Sunday); default: the week that just ended. */
  week: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type OwnerBriefQuery = z.infer<typeof OwnerBriefQuerySchema>;

export const OwnerBriefSchema = z.object({
  id: z.uuid(),
  weekStart: z.string(),
  weekEnd: z.string(),
  generatedAt: z.string(),
  scope: ScopeInfoSchema,
  decisions: z.array(BriefDecisionSchema),
  /** Plain sentences when fewer than three decisions were found (no data, all on track). */
  notes: z.array(z.string()),
  engine: BriefEngineSchema,
  email: BriefEmailSchema,
  /** Stored by the analytics worker (absent on a Rebuild, which is computed in the request). */
  snapshot: SnapshotInfoSchema.optional(),
});
export type OwnerBrief = z.infer<typeof OwnerBriefSchema>;

export const InsightSubscriptionsSchema = z.object({
  /** Monday email of the Owner Weekly Brief. */
  ownerBrief: z.boolean(),
  /** Email (and ntfy, when configured) for new KPI alerts. */
  kpiAlerts: z.boolean(),
});
export type InsightSubscriptions = z.infer<typeof InsightSubscriptionsSchema>;

export const InsightSubscriptionsViewSchema = InsightSubscriptionsSchema.extend({
  /** True while the Automations & notifications gate holds every staff email. */
  gateOff: z.boolean(),
  gateNote: z.string().nullable(),
});
export type InsightSubscriptionsView = z.infer<typeof InsightSubscriptionsViewSchema>;

/* ------------------------------------------------------------------------ */
/* Monday owner pack (owner role test, automation 1)                         */
/* ------------------------------------------------------------------------ */

export const OwnerPackQuerySchema = z.object({
  /** Any day of the week to report (default: the last complete week). */
  week: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

const PackFigures = z.object({
  /** Money is null without money.view at the office. */
  productionWeekCents: z.number().int().nullable(),
  productionMtdCents: z.number().int().nullable(),
  productionGoalCents: z.number().int().nullable(),
  collectionsWeekCents: z.number().int().nullable(),
  collectionsMtdCents: z.number().int().nullable(),
  collectionsGoalCents: z.number().int().nullable(),
  /** The one Total A/R and its share over 90 days (ledger offices only). */
  arTotalCents: z.number().int().nullable(),
  arOver90Cents: z.number().int().nullable(),
  /** Case acceptance (dollars), month to date. */
  caseAcceptance: z.number().nullable(),
  newPatientsWeek: z.number().int().nullable(),
  /** Provider hours booked in the week (BOOKED_SHARE). */
  bookedShareWeek: z.number().nullable(),
});

export const OwnerPackSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  month: z.string(),
  generatedAt: z.string(),
  offices: z.array(PackFigures.extend({ locationId: z.uuid(), name: z.string() })),
  total: PackFigures,
  /** The week's brief decisions (the same as the Weekly brief page). */
  decisions: z.array(
    z.object({
      rank: z.number().int(),
      locationId: z.uuid().nullable(),
      headline: z.string(),
      question: z.string(),
      action: z.string(),
    }),
  ),
  notes: z.array(z.string()),
  /** Email delivery: held while Settings > Automations & notifications is off. */
  email: z.object({ state: z.string(), note: z.string().nullable() }),
  /** The figures are stored by the analytics worker. */
  snapshot: SnapshotInfoSchema.optional(),
});
export type OwnerPack = z.infer<typeof OwnerPackSchema>;
